import React, { useEffect, useRef, useState } from 'react';
import LogoSurface from './LogoSurface';
import CompactLoaderMark from './CompactLoaderMark';
import { useDelayedReveal } from './loadingPolicy';
import './BrandLoader.scss';

const assetRoot = `${process.env.PUBLIC_URL || ''}/brand`;
const reduceMotion = () => Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

// Compact rings are CSS-only and should not subscribe to video/motion events.
function AnimatedLogo({ visible }) {
  const [reduced, setReduced] = useState(reduceMotion);
  const [playing, setPlaying] = useState(false);
  const [frameReady, setFrameReady] = useState(false);
  const video = useRef(null);
  const canvas = useRef(null);

  useEffect(() => {
    const preference = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const update = () => { setReduced(Boolean(preference?.matches)); setPlaying(false); };
    preference?.addEventListener?.('change', update);
    return () => preference?.removeEventListener?.('change', update);
  }, []);

  useEffect(() => {
    const media = video.current;
    if (!media) return;
    let disposed = false;
    let attempt = 0;
    const update = () => {
      const currentAttempt = ++attempt;
      if (document.hidden || !visible) media.pause();
      else media.play()?.catch(() => {
        // Safari may reject the previous play() after a pause/resume. That
        // stale promise must not hide frames from the playback now running.
        if (!disposed && currentAttempt === attempt) setPlaying(false);
      });
    };
    update();
    document.addEventListener('visibilitychange', update);
    return () => { disposed = true; document.removeEventListener('visibilitychange', update); media.pause(); };
  }, [visible, reduced]);

  // iOS Safari composites video outside SVG filters. Paint its decoded frames
  // onto a canvas so the same transparent matte works on every browser.
  useEffect(() => {
    const media = video.current;
    const surface = canvas.current;
    if (!playing || !visible || reduced || !media || !surface) {
      setFrameReady(false);
      return;
    }
    const context = surface.getContext('2d');
    if (!context) return;
    const decodedFrames = typeof media.requestVideoFrameCallback === 'function';
    let callback;
    let stopped = false;
    let painted = false;
    let contextLost = false;
    let lastPaint = -Infinity;
    const loseContext = () => { contextLost = true; painted = false; setFrameReady(false); };
    const restoreContext = () => { contextLost = false; };
    surface.addEventListener('contextlost', loseContext);
    surface.addEventListener('contextrestored', restoreContext);
    const paint = (time = 0) => {
      if (stopped) return;
      if (!contextLost && !document.hidden && media.readyState >= 2 && (decodedFrames || time - lastPaint >= 80)) {
        try {
          context.drawImage(media, 0, 0, surface.width, surface.height);
          lastPaint = time;
          if (!painted) { painted = true; setFrameReady(true); }
        } catch {
          // Decoders can temporarily expose an unavailable frame while
          // buffering/resuming. Keep the last image and try the next frame.
          // Stopping this loop permanently froze an otherwise playing logo.
        }
      }
      callback = decodedFrames ? media.requestVideoFrameCallback(paint) : requestAnimationFrame(paint);
    };
    // `playing` can fire before the decoder has presented its first frame.
    // Keep the poster until that frame callback; an eager draw can succeed
    // with an empty canvas during route remounts, especially on mobile GPUs.
    callback = decodedFrames ? media.requestVideoFrameCallback(paint) : requestAnimationFrame(paint);
    return () => {
      stopped = true;
      surface.removeEventListener('contextlost', loseContext);
      surface.removeEventListener('contextrestored', restoreContext);
      if (decodedFrames) media.cancelVideoFrameCallback(callback);
      else cancelAnimationFrame(callback);
    };
  }, [playing, visible, reduced]);

  return (
    <LogoSurface className="brand-loader__mark">
        <img src={`${assetRoot}/wiw-loader-poster.png`} alt="" width="512" height="512"
          data-hidden={frameReady && playing && !reduced} />
        {!reduced && <video ref={video} muted loop playsInline preload="auto" autoPlay={visible}
          poster={`${assetRoot}/wiw-loader-poster.png`} tabIndex={-1}
          data-playing={playing} onPlaying={() => setPlaying(true)} onError={() => setPlaying(false)}>
          <source src={`${assetRoot}/wiw-loader.mp4`} type="video/mp4" />
        </video>}
        {!reduced && <canvas ref={canvas} width="512" height="512" data-ready={frameReady && playing} />}
    </LogoSurface>
  );
}

// No translation hook: this also serves as the initial i18n Suspense fallback.
export default function BrandLoader({ compact = false, fullPage = false, label = 'Loading', delay, className = '' }) {
  const visible = useDelayedReveal(delay);

  return (
    <div className={`brand-loader${compact ? ' brand-loader--compact' : ' brand-loader--screen'}${fullPage ? ' brand-loader--page' : ''} ${className}`.trim()}
      role="status" aria-live="polite" aria-label={label} aria-busy="true" data-visible={visible}>
      {visible && (compact ? <CompactLoaderMark delay={0} /> : <AnimatedLogo visible />)}
    </div>
  );
}
